import { takeLatest, put, call } from 'redux-saga/effects';
import authService from '../../services/authService';
import {
  loginRequest,
  loginSuccess,
  loginFailure,
  registerRequest,
  registerSuccess,
  registerFailure,
  logout,
  restoreAuth,
  setLoading,
  checkAuth,
  refreshTokenRequest,
  refreshTokenSuccess,
  refreshTokenFailure
} from './authSlice';
import { setUserId } from '../chatbot/chatbotSlice';

function* handleLogin(action) {
  try {
    const { email, password } = action.payload;
    const result = yield call([authService, authService.login], email, password);
    // result.user is the profile returned from backend
    yield put(loginSuccess(result.user));
    // Set userId in chatbot state (convert to string as backend expects string in header)
    if (result.user?.id) {
      yield put(setUserId(String(result.user.id)));
    }
  } catch (error) {
    yield put(loginFailure(error.message || 'Login failed'));
  }
}

function* handleRegister(action) {
  try {
    const userData = action.payload;
    const user = yield call([authService, authService.register], userData);
    yield put(registerSuccess(user));
  } catch (error) {
    // Extract error message properly
    const errorMessage = error?.message || error?.detail || (typeof error === 'string' ? error : 'Registration failed');
    yield put(registerFailure(errorMessage));
  }
}

function* handleLogout() {
  try {
    yield call([authService, authService.logout]);
    // Clear userId from chatbot state on logout
    yield put(setUserId(null));
  } catch (e) {
    // Ignore logout errors; tokens are cleared in service
    // Still clear userId
    yield put(setUserId(null));
  }
}

function* handleCheckAuth() {
  try {
    yield put(setLoading(true));
    
    // Check if user has valid tokens
    if (authService.isAuthenticated()) {
      try {
        // Try to get user profile to verify token is still valid
        const userProfile = yield call([authService, authService.getUserProfile]);
        yield put(restoreAuth({ user: userProfile, isAuthenticated: true }));
        // Set userId in chatbot state (convert to string as backend expects string in header)
        if (userProfile?.id) {
          yield put(setUserId(String(userProfile.id)));
        }
      } catch (error) {
        // If token is invalid or expired, try to refresh
        try {
          yield call([authService, authService.refreshToken]);
          const userProfile = yield call([authService, authService.getUserProfile]);
          yield put(restoreAuth({ user: userProfile, isAuthenticated: true }));
          // Set userId in chatbot state (convert to string as backend expects string in header)
          if (userProfile?.id) {
            yield put(setUserId(String(userProfile.id)));
          }
        } catch (refreshError) {
          // If refresh also fails, clear auth state
          yield put(restoreAuth({ user: null, isAuthenticated: false }));
          yield put(setUserId(null));
        }
      }
    } else {
      yield put(restoreAuth({ user: null, isAuthenticated: false }));
      yield put(setUserId(null));
    }
  } catch (error) {
    // If anything fails, clear auth state
    yield put(restoreAuth({ user: null, isAuthenticated: false }));
    yield put(setUserId(null));
  } finally {
    yield put(setLoading(false));
  }
}

// ==========================================
// NEW: Silent Background Refresh Saga
// ==========================================
function* handleRefreshToken() {
  try {
    yield call([authService, authService.refreshToken]);
    
    // If successful, dispatch success so Redux knows it worked
    yield put(refreshTokenSuccess());
  } catch (error) {
    console.error("Background silent refresh failed:", error);
    
    // If the refresh fails (e.g. they left the tab open for 3 days and the refresh token expired)
    // We update Redux to failure and force a logout to protect the app.
    yield put(refreshTokenFailure());
    yield put(logout());
  }
}

export function* watchAuthSaga() {
  yield takeLatest(loginRequest.type, handleLogin);
  yield takeLatest(registerRequest.type, handleRegister);
  yield takeLatest(logout.type, handleLogout);
  yield takeLatest(checkAuth.type, handleCheckAuth);
  
  yield takeLatest(refreshTokenRequest.type, handleRefreshToken);
}